import Cookies from 'js-cookie'
import axios from 'axios'
import { userAgentChainInterceptor } from '@fs/zion-axios-base'
import zionDebug from '@fs/zion-debug'
import { getCachedSession, setCachedSession } from './cache'
import { getCorrelationId, getSessionId } from './index'
import getIp from './ip'

const debug = zionDebug('zion:session:fetch')

const CLIENT_DEV_KEY = 'a02f100000TnN56AAF'
const sessionUrl = '/service/ident/session/sessions'
const anonSessionUrl = '/service/ident/cis/cis-web/oauth2/v3/token'
let pendingPromise = false

// Transform session response format to old format
function transformSessionResponse(data) {
  const { assuranceData, sessionId, userId, sessionValues, ...rest } = data

  // Transform session values from object to array format
  const values = []
  if (sessionValues?.transientValues) {
    Object.entries(sessionValues.transientValues).forEach(([name, value]) => {
      values.push({
        name,
        value,
        restricted: false,
      })
    })
  }
  if (sessionValues?.restrictedValues) {
    Object.entries(sessionValues.restrictedValues).forEach(([name, value]) => {
      values.push({
        name,
        value,
        restricted: true,
      })
    })
  }

  return {
    authentication: null,
    session: {
      assuranceData: {
        ...assuranceData,
        dataAsString: `${assuranceData.level} ${assuranceData.protocol} ${assuranceData.provider}`,
      },
      id: sessionId,
      ipAddress: rest.ipAddress,
      developerKey: rest.developerKey,
      proxyId: rest.proxyId ?? null,
      proxyType: rest.proxyType ?? null,
      values,
    },
    users: [
      {
        type: 'userAccount',
        accountType: null,
        birthDate: null,
        contactName: null,
        country: null,
        deletedDate: null,
        displayMembershipNumber: null,
        displayName: null,
        email: null,
        enabled: null,
        familyName: null,
        gender: null,
        givenName: null,
        helperPin: null,
        id: userId,
        membershipNumber: null,
        oldPassword: null,
        parentalConsent: null,
        password: null,
        positions: null,
        preferences: null,
        preferredLanguage: null,
        sms: null,
        unVerified: {
          emailAddress: null,
        },
        updateDate: null,
        username: null,
        ward: null,
      },
    ],
  }
}

export async function getSession(ignoreCache) {
  const fssessionid = getSessionId()
  debug('getSession called', { fssessionid: getCorrelationId(fssessionid), ignoreCache })

  // if no fssessionid cookie
  if (!fssessionid) {
    return false
  }
  if (!ignoreCache) {
    const cachedSession = getCachedSession(fssessionid)

    if (cachedSession) {
      return cachedSession
    }
  }

  const config = await userAgentChainInterceptor({
    headers: {
      accept: 'application/session-v1+json',
      authorization: `Bearer ${fssessionid}`,
    },
    fsUserAgentChainSuffix: 'zion-session',
  })

  pendingPromise = pendingPromise || axios.get(`${sessionUrl}/CURRENT`, config)

  try {
    const { data } = await pendingPromise

    if (data?.sessionId) {
      const transformedData = transformSessionResponse(data)
      setCachedSession(transformedData)
      return transformedData
    }
  } finally {
    pendingPromise = false
  }
  return false
}

export async function getNewAnonSession(ipAddress, devKey) {
  debug('getNewAnonSession called', { ipAddress, devKey })

  if (!ipAddress || !devKey) {
    throw new Error('ipAddress and devKey required')
  }
  let currSession = false
  try {
    const sessionRes = await getSession()

    if (sessionRes.session) {
      // valid user session
      currSession = sessionRes
    }
  } catch (error) {
    debug('Error checking existing session, continuing with anon session', error)
    // if no valid session, move along
    // console.error('getCurrentSession Error:', error);
  }
  if (currSession) {
    // if user session valid, don't continue to make an anon session
    return currSession.session
  }

  const body = `grant_type=unauthenticated_session&ip_address=${ipAddress}&client_id=${devKey}`
  const anonOptions = await userAgentChainInterceptor({
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    fsUserAgentChainSuffix: 'zion-session',
  })

  const { data } = await axios.post(anonSessionUrl, body, anonOptions)

  if (data && data.token) {
    let cookieDomain
    if (typeof window !== 'undefined' && window.location.hostname.match('familysearch.org')) {
      cookieDomain = '.familysearch.org'
    }
    Cookies.set('fssessionid', data.token, { domain: cookieDomain })
    return { id: data.token }
  }
  throw new Error('No session found.')
}

export async function getAnonSession() {
  // get ip
  const ip = await getIp()
  // get devkey
  const devKey = CLIENT_DEV_KEY
  // get new session
  if (!ip || !devKey) {
    throw new Error('getAnonSession requires valid ip and devkey')
  }
  const anonSession = await getNewAnonSession(ip, devKey)

  return anonSession
}
